import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';
import { ClockIcon, UserGroupIcon, BoltIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import api from '../../api/client';
import { etTodayISO } from '../../lib/etTime';

// Admin oversight of the staff time clock. Shows who's currently clocked
// in (with live elapsed time), plus today's full event log across all
// staff. Refreshes every 30 seconds.

function formatElapsed(startISO, now = Date.now()) {
  const ms = now - new Date(startISO).getTime();
  const totalSec = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

function formatTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit' });
}

const EVENT_META = {
  clock_in:                { label: 'Clocked in',          cls: 'bg-green-100 text-green-700' },
  clock_out:               { label: 'Clocked out',         cls: 'bg-gray-100 text-gray-700' },
  late_arrival:            { label: '🟡 Late arrival',     cls: 'bg-amber-100 text-amber-700' },
  late_return_from_break:  { label: '🟡 Long lunch',       cls: 'bg-amber-100 text-amber-700' },
};

export default function AdminTimeClockPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(Date.now());

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/time-clock');
      setData(data);
    } catch (err) {
      toast.error('Failed to load time clock');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000); // tick for elapsed
    return () => clearInterval(id);
  }, []);
  useEffect(() => {
    const id = setInterval(load, 30 * 1000); // refresh full data every 30s
    return () => clearInterval(id);
  }, [load]);

  if (loading) {
    return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" /></div>;
  }
  if (!data) return null;

  const clockedIn = data.staff.filter(s => s.latest_event_type === 'clock_in');
  const offClock  = data.staff.filter(s => s.latest_event_type !== 'clock_in');

  return (
    <div className="animate-slide-up">
      <Toaster position="top-right" />

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Time Clock</h1>
          <p className="text-gray-500 text-sm">
            {clockedIn.length} currently clocked in · refreshes every 30 seconds
          </p>
        </div>
      </div>

      {/* Currently clocked in */}
      <div className="bg-white border border-gray-200 rounded-2xl p-5 mb-6">
        <h2 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <BoltIcon className="w-5 h-5 text-green-500" /> Currently Clocked In ({clockedIn.length})
        </h2>
        {clockedIn.length === 0 ? (
          <p className="text-sm text-gray-500">No one is currently on the clock.</p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {clockedIn.map(s => (
              <Link key={s.id} to={`/staff/${s.id}`}
                className="bg-green-50 border border-green-200 rounded-xl p-3 hover:bg-green-100 transition-colors">
                <p className="font-semibold text-gray-900">{s.first_name} {s.last_name}</p>
                <p className="text-xs text-gray-500 mt-0.5">{s.role}</p>
                <p className="text-sm text-green-700 mt-2 font-mono">
                  {formatElapsed(s.latest_event_at, now)} (since {formatTime(s.latest_event_at)})
                </p>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Recent failed clock attempts — answers "why couldn't X clock in?" */}
      <ClockIssuesPanel />

      {/* Off-clock today (worked + clocked out, or didn't clock in) */}
      {offClock.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-2xl p-5 mb-6">
          <h2 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <UserGroupIcon className="w-5 h-5 text-gray-400" /> Off-clock Today ({offClock.length})
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
            {offClock.map(s => (
              <Link key={s.id} to={`/staff/${s.id}`}
                className="border border-gray-200 rounded-lg px-3 py-2 hover:bg-gray-50">
                <p className="font-medium text-gray-900">{s.first_name} {s.last_name}</p>
                <p className="text-xs text-gray-500">
                  {s.latest_event_at
                    ? `Last: ${EVENT_META[s.latest_event_type]?.label || s.latest_event_type} ${formatTime(s.latest_event_at)}`
                    : 'No events today'}
                </p>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Today's full event log */}
      <div className="bg-white border border-gray-200 rounded-2xl p-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold text-gray-900 flex items-center gap-2">
            <ClockIcon className="w-5 h-5 text-gray-400" /> Today's Events
          </h2>
          <RepairLateArrivalsButton onDone={load} />
        </div>
        {data.todays_events.length === 0 ? (
          <p className="text-sm text-gray-500">No events recorded yet today.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500 uppercase tracking-wider border-b border-gray-100">
                <th className="pb-2 font-medium">Time</th>
                <th className="pb-2 font-medium">Staff</th>
                <th className="pb-2 font-medium">Event</th>
                <th className="pb-2 font-medium">Notes</th>
              </tr>
            </thead>
            <tbody>
              {data.todays_events.map((ev, i) => {
                const meta = EVENT_META[ev.event_type] || { label: ev.event_type, cls: 'bg-gray-100 text-gray-700' };
                return (
                  <tr key={i} className="border-b border-gray-50">
                    <td className="py-2 text-gray-700 font-mono text-xs">{formatTime(ev.occurred_at)}</td>
                    <td className="py-2 text-gray-900">{ev.first_name} {ev.last_name}</td>
                    <td className="py-2">
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${meta.cls}`}>
                        {meta.label}
                      </span>
                    </td>
                    <td className="py-2 text-gray-600 text-xs">{ev.notes || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// One-shot repair for the UTC-vs-ET bug that recorded on-time clock-ins
// as ~240-minute late arrivals. Recomputes today's late arrivals against
// the correct ET shift start and PERMANENTLY DELETES the false ones along
// with their point-ledger entries and wallet events (refunding the points).
//
// Every run now writes a forensic activity_log record (`timeclock.repair_day`)
// holding the full pre-image of every row it deletes or rewrites, so a run can
// be reconstructed by hand. Re-running is safe in the sense that already-fixed
// rows no longer match — but each run destroys rows irreversibly, so it is not
// a no-op and should not be treated as one.
function RepairLateArrivalsButton({ onDone }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (!window.confirm(
      "Recompute today's late-arrival records using the correct Eastern-time math?\n\n" +
      "This PERMANENTLY DELETES attendance records, their attendance-point ledger " +
      "entries, and the matching wallet events for anyone the recompute finds " +
      "wasn't actually late. Points are refunded. Genuine late arrivals are " +
      "rewritten in place.\n\n" +
      "There is no undo. A record of exactly what was removed is written to the " +
      "activity log."
    )) return;
    setBusy(true);
    try {
      // Eastern, not the browser's clock: this names the business day whose
      // records get destroyed, and near midnight local time would pick the
      // wrong one.
      const dateISO = etTodayISO();
      const { data } = await api.post(`/time-clock/repair-day?date=${dateISO}`);
      toast.success(
        `Cleaned up ${data.late_arrivals_deleted} false late arrival${data.late_arrivals_deleted === 1 ? '' : 's'}` +
        (data.late_arrivals_updated ? ` · fixed ${data.late_arrivals_updated} genuine one${data.late_arrivals_updated === 1 ? '' : 's'}` : '') +
        (data.wallet_points_refunded ? ` · refunded ${data.wallet_points_refunded} pts` : ''),
        { duration: 6000 }
      );
      onDone && onDone();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Repair failed');
    } finally { setBusy(false); }
  };
  return (
    <button onClick={run} disabled={busy}
      title="Recompute today's (Eastern) late arrivals with the correct ET math. Permanently deletes the false ones, their point-ledger entries and wallet events, and refunds the points. No undo."
      className="inline-flex items-center gap-1 text-xs font-semibold text-amber-800 bg-amber-50 hover:bg-amber-100 border border-amber-200 px-3 py-1.5 rounded-lg disabled:opacity-50">
      <BoltIcon className="w-3.5 h-3.5" />
      {busy ? 'Repairing…' : "Repair today's late arrivals"}
    </button>
  );
}

// Recent blocked/errored clock attempts, so a director can see WHO couldn't
// clock in and WHY (too early, already clocked in, kiosk token, server error)
// instead of hearing it as a complaint. Fed by GET /api/time-clock/audit.
const REASON_LABEL = {
  already_clocked_in: 'Already clocked in',
  not_clocked_in: "Wasn't clocked in",
};
function prettyReason(r) {
  if (!r) return '—';
  if (REASON_LABEL[r]) return REASON_LABEL[r];
  if (r.startsWith('too_early')) return 'Too early for shift';
  return r; // kiosk reason or raw error — show as-is
}
function ClockIssuesPanel() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    api.get('/time-clock/audit?days=7&outcome=issues')
      .then(({ data }) => setData(data))
      .catch(() => { /* non-fatal */ });
  }, []);

  if (!data) return null;
  const attempts = data.attempts || [];
  const fmt = (iso) => new Date(iso).toLocaleString('en-US', {
    timeZone: 'America/New_York', weekday: 'short', hour: 'numeric', minute: '2-digit',
  });

  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-5 mb-6">
      <button onClick={() => setOpen(o => !o)} className="w-full flex items-center justify-between text-left">
        <h2 className="font-semibold text-gray-900 flex items-center gap-2">
          <ExclamationTriangleIcon className={`w-5 h-5 ${attempts.length ? 'text-amber-500' : 'text-gray-300'}`} />
          Clock Issues — last 7 days ({attempts.length})
        </h2>
        {attempts.length > 0 && <span className="text-xs text-gray-400">{open ? 'Hide' : 'Show'}</span>}
      </button>

      {attempts.length === 0 ? (
        <p className="text-sm text-gray-500 mt-2">No failed clock attempts in the last 7 days.</p>
      ) : open && (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500 uppercase tracking-wider border-b border-gray-100">
                <th className="pb-2 font-medium">When</th>
                <th className="pb-2 font-medium">Staff</th>
                <th className="pb-2 font-medium">Action</th>
                <th className="pb-2 font-medium">Why</th>
                <th className="pb-2 font-medium text-right">Status</th>
              </tr>
            </thead>
            <tbody>
              {attempts.map((a) => (
                <tr key={a.id} className="border-b border-gray-50">
                  <td className="py-2 text-gray-600 text-xs whitespace-nowrap">{fmt(a.created_at)}</td>
                  <td className="py-2 text-gray-900">
                    {a.first_name ? `${a.first_name} ${a.last_name}` : `Staff #${a.staff_id ?? '—'}`}
                  </td>
                  <td className="py-2 text-gray-600 text-xs">{a.action === 'clock_in' ? 'Clock in' : 'Clock out'}</td>
                  <td className="py-2 text-gray-700 text-xs">
                    {prettyReason(a.reason)}
                    {a.duration_ms > 3000 && <span className="text-amber-600"> · slow {Math.round(a.duration_ms / 1000)}s</span>}
                  </td>
                  <td className="py-2 text-right">
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                      a.outcome === 'error' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
                    }`}>
                      {a.outcome === 'error' ? 'Error' : 'Blocked'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
